import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  MessageSquare,
  Clock,
  Users,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Loader2,
  RefreshCw,
  Calendar,
  Globe,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  MessageCircle,
  UserCheck,
  Timer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput, TimeInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Common timezones
const TIMEZONES = [
  { value: 'Asia/Dubai', label: 'Dubai (GST, UTC+4)' },
  { value: 'Asia/Riyadh', label: 'Riyadh (AST, UTC+3)' },
  { value: 'Asia/Kolkata', label: 'India (IST, UTC+5:30)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT, UTC+8)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const AUTO_ASSIGN_METHODS = [
  { value: 'round_robin', label: 'Round Robin', description: 'Assign to agents in rotation' },
  { value: 'least_busy', label: 'Least Busy', description: 'Assign to agent with fewest open conversations' },
  { value: 'random', label: 'Random', description: 'Randomly assign to available agents' },
];

// ============================================================================
// CANNED RESPONSES TAB
// ============================================================================
const CannedResponsesTab = () => {
  const [responses, setResponses] = useState([]);
  const [categories, setCategories] = useState(['general']);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    shortcut: '',
    category: 'general'
  });
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/canned-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponses(response.data.responses || []);
      setCategories(response.data.categories || ['general']);
    } catch (error) {
      toast.error('Failed to load canned responses');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingResponse) {
        await axios.put(
          `${API_BASE_URL}/inbox/canned-responses/${editingResponse.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Canned response updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/inbox/canned-responses`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Canned response created');
      }
      
      setDialogOpen(false);
      resetForm();
      loadResponses();
    } catch (error) {
      toast.error('Failed to save canned response');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this canned response?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/inbox/canned-responses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Canned response deleted');
      loadResponses();
    } catch (error) {
      toast.error('Failed to delete canned response');
    }
  };

  const openEditDialog = (response) => {
    setEditingResponse(response);
    setFormData({
      title: response.title,
      content: response.content,
      shortcut: response.shortcut || '',
      category: response.category || 'general'
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingResponse(null);
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', shortcut: '', category: 'general' });
    setEditingResponse(null);
  };

  const filteredResponses = categoryFilter === 'all' 
    ? responses 
    : responses.filter(r => r.category === categoryFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Canned Responses</h3>
          <p className="text-sm text-wt-text-muted">Quick replies that can be used when chatting with customers</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-36 bg-wt-surface border-wt-border text-wt-text-primary">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              <SelectItem value="all" className="text-wt-text-primary">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat} className="text-wt-text-primary capitalize">
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={openCreateDialog}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            data-testid="add-canned-response-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Response
          </Button>
        </div>
      </div>

      {filteredResponses.length === 0 ? (
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-wt-text-muted mb-4" />
            <p className="text-wt-text-muted">No canned responses yet</p>
            <Button onClick={openCreateDialog} className="mt-4 bg-wt-gold hover:bg-wt-gold-hover text-black">
              <Plus className="h-4 w-4 mr-2" />
              Create First Response
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredResponses.map((response) => (
            <Card key={response.id} className="bg-wt-surface border-wt-border hover:border-wt-gold/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-wt-text-primary">{response.title}</h4>
                      {response.shortcut && (
                        <Badge variant="outline" className="text-xs border-wt-border text-wt-text-muted">
                          {response.shortcut}
                        </Badge>
                      )}
                      <Badge className="text-xs bg-wt-subtle text-wt-text-secondary capitalize">
                        {response.category}
                      </Badge>
                    </div>
                    <p className="text-sm text-wt-text-muted line-clamp-2">{response.content}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(response)}
                      className="text-wt-text-muted hover:text-wt-gold"
                      data-testid={`edit-response-${response.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(response.id)}
                      className="text-wt-text-muted hover:text-red-400"
                      data-testid={`delete-response-${response.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              {editingResponse ? 'Edit Canned Response' : 'New Canned Response'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-wt-text-primary">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Greeting, Order Status"
                className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="canned-response-title"
              />
            </div>
            <div>
              <Label className="text-wt-text-primary">Message Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Hello! How can I help you today?"
                rows={4}
                className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="canned-response-content"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Shortcut</Label>
                <Input
                  value={formData.shortcut}
                  onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                  placeholder="/greeting"
                  className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted mt-1">Type this in chat to use</p>
              </div>
              <div>
                <Label className="text-wt-text-primary">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({ ...formData, category: val })}
                >
                  <SelectTrigger className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-wt-text-primary capitalize">
                        {cat}
                      </SelectItem>
                    ))}
                    <SelectItem value="greeting" className="text-wt-text-primary">Greeting</SelectItem>
                    <SelectItem value="support" className="text-wt-text-primary">Support</SelectItem>
                    <SelectItem value="sales" className="text-wt-text-primary">Sales</SelectItem>
                    <SelectItem value="closing" className="text-wt-text-primary">Closing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="save-canned-response-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingResponse ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// WORKING HOURS TAB
// ============================================================================
const WorkingHoursTab = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    timezone: 'Asia/Dubai',
    schedule: {
      monday: { start: '09:00', end: '18:00', enabled: true },
      tuesday: { start: '09:00', end: '18:00', enabled: true },
      wednesday: { start: '09:00', end: '18:00', enabled: true },
      thursday: { start: '09:00', end: '18:00', enabled: true },
      friday: { start: '09:00', end: '18:00', enabled: true },
      saturday: { start: '09:00', end: '14:00', enabled: true },
      sunday: { start: '00:00', end: '00:00', enabled: false }
    },
    away_message: "Thank you for reaching out! We're currently outside our working hours. We'll respond as soon as we're back.",
    holiday_message: "We're currently on holiday. We'll respond when we return.",
    holidays: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/inbox/settings/working-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load working hours settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/inbox/settings/working-hours`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Working hours settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value
        }
      }
    }));
  };

  const addHoliday = () => {
    if (!newHoliday) return;
    if (settings.holidays.includes(newHoliday)) {
      toast.error('Holiday already added');
      return;
    }
    setSettings(prev => ({
      ...prev,
      holidays: [...prev.holidays, newHoliday]
    }));
    setNewHoliday('');
  };

  const removeHoliday = (date) => {
    setSettings(prev => ({
      ...prev,
      holidays: prev.holidays.filter(h => h !== date)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Working Hours</h3>
          <p className="text-sm text-wt-text-muted">Configure business hours and auto-reply messages</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          data-testid="save-working-hours-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Enable/Disable */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-wt-text-primary">Enable Working Hours</h4>
              <p className="text-sm text-wt-text-muted">Send auto-reply when outside business hours</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="working-hours-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Timezone */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-wt-text-primary">
            <Globe className="h-4 w-4" />
            Timezone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select 
            value={settings.timezone} 
            onValueChange={(val) => setSettings(prev => ({ ...prev, timezone: val }))}
          >
            <SelectTrigger className="w-full bg-wt-bg border-wt-border text-wt-text-primary" data-testid="timezone-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value} className="text-wt-text-primary">
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-wt-text-primary">
            <Clock className="h-4 w-4" />
            Weekly Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {DAYS_OF_WEEK.map(day => (
            <div key={day.key} className="flex items-center gap-4 py-2 border-b border-wt-border last:border-0">
              <div className="w-28">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={settings.schedule[day.key]?.enabled}
                    onCheckedChange={(checked) => updateDaySchedule(day.key, 'enabled', checked)}
                    className="border-wt-border data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                  />
                  <span className={`text-sm ${settings.schedule[day.key]?.enabled ? 'text-wt-text-primary' : 'text-wt-text-muted'}`}>
                    {day.label}
                  </span>
                </label>
              </div>
              {settings.schedule[day.key]?.enabled ? (
                <div className="flex items-center gap-2">
                  <TimeInput
                    value={settings.schedule[day.key]?.start || '09:00'}
                    onChange={(e) => updateDaySchedule(day.key, 'start', e.target.value)}
                    className="w-32 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                  <span className="text-wt-text-muted">to</span>
                  <TimeInput
                    value={settings.schedule[day.key]?.end || '18:00'}
                    onChange={(e) => updateDaySchedule(day.key, 'end', e.target.value)}
                    className="w-32 bg-wt-bg border-wt-border text-wt-text-primary"
                  />
                </div>
              ) : (
                <span className="text-sm text-wt-text-muted">Closed</span>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Away Message */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-wt-text-primary">Away Message</CardTitle>
          <CardDescription className="text-wt-text-muted">Auto-reply sent outside business hours</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={settings.away_message}
            onChange={(e) => setSettings(prev => ({ ...prev, away_message: e.target.value }))}
            rows={3}
            className="bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="away-message-input"
          />
        </CardContent>
      </Card>

      {/* Holidays */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-wt-text-primary">
            <Calendar className="h-4 w-4" />
            Holidays
          </CardTitle>
          <CardDescription className="text-wt-text-muted">Specific dates when business is closed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <DateInput
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              className="flex-1 bg-wt-bg border-wt-border text-wt-text-primary"
            />
            <Button onClick={addHoliday} size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {settings.holidays.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {settings.holidays.map(date => (
                <Badge key={date} variant="outline" className="border-wt-border text-wt-text-primary pr-1">
                  {date}
                  <button onClick={() => removeHoliday(date)} className="ml-2 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div>
            <Label className="text-wt-text-primary">Holiday Message</Label>
            <Textarea
              value={settings.holiday_message}
              onChange={(e) => setSettings(prev => ({ ...prev, holiday_message: e.target.value }))}
              rows={2}
              className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// AUTO-ASSIGN TAB
// ============================================================================
const AutoAssignTab = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    method: 'round_robin',
    team_members: [],
    max_conversations_per_user: 0,
    assign_to_online_only: false,
    priority_customers: [],
    fallback_user_id: null
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, teamRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/inbox/settings/auto-assign`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/inbox/team-members`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSettings(settingsRes.data);
      setTeamMembers(teamRes.data.users || []);
    } catch (error) {
      console.error('Failed to load auto-assign settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/inbox/settings/auto-assign`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Auto-assign settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleTeamMember = (userId) => {
    const currentMembers = settings.team_members || [];
    if (currentMembers.includes(userId)) {
      setSettings(prev => ({
        ...prev,
        team_members: currentMembers.filter(id => id !== userId)
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        team_members: [...currentMembers, userId]
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Auto-Assign Rules</h3>
          <p className="text-sm text-wt-text-muted">Automatically assign new conversations to team members</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          data-testid="save-auto-assign-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Enable/Disable */}
      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-wt-text-primary">Enable Auto-Assignment</h4>
              <p className="text-sm text-wt-text-muted">Automatically assign new conversations to agents</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="auto-assign-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Assignment Method */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-wt-text-primary">Assignment Method</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {AUTO_ASSIGN_METHODS.map(method => (
            <label 
              key={method.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                settings.method === method.value 
                  ? 'border-wt-gold bg-wt-gold/10' 
                  : 'border-wt-border hover:border-wt-gold/50'
              }`}
            >
              <input
                type="radio"
                name="assign_method"
                value={method.value}
                checked={settings.method === method.value}
                onChange={(e) => setSettings(prev => ({ ...prev, method: e.target.value }))}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-wt-text-primary">{method.label}</span>
                <p className="text-sm text-wt-text-muted">{method.description}</p>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* Team Members */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-wt-text-primary">
            <Users className="h-4 w-4" />
            Team Members
          </CardTitle>
          <CardDescription className="text-wt-text-muted">
            Select agents who will receive auto-assigned conversations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {teamMembers.length === 0 ? (
              <p className="text-sm text-wt-text-muted py-4 text-center">No team members found</p>
            ) : (
              teamMembers.map(member => (
                <label
                  key={member.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    settings.team_members?.includes(member.id)
                      ? 'border-wt-gold bg-wt-gold/10'
                      : 'border-wt-border hover:border-wt-gold/50'
                  }`}
                >
                  <Checkbox
                    checked={settings.team_members?.includes(member.id)}
                    onCheckedChange={() => toggleTeamMember(member.id)}
                    className="border-wt-border data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-wt-text-primary">{member.full_name}</span>
                    <p className="text-xs text-wt-text-muted">{member.email}</p>
                  </div>
                  {settings.team_members?.includes(member.id) && (
                    <CheckCircle className="h-4 w-4 text-wt-gold" />
                  )}
                </label>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-wt-text-primary">Advanced Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-wt-text-primary">Max Conversations Per Agent</Label>
            <Input
              type="number"
              min="0"
              value={settings.max_conversations_per_user}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                max_conversations_per_user: parseInt(e.target.value) || 0 
              }))}
              className="mt-1.5 w-32 bg-wt-bg border-wt-border text-wt-text-primary"
            />
            <p className="text-xs text-wt-text-muted mt-1">0 = unlimited</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-wt-text-primary">Assign to Online Only</h4>
              <p className="text-sm text-wt-text-muted">Only assign to agents who are currently online</p>
            </div>
            <Switch
              checked={settings.assign_to_online_only}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, assign_to_online_only: checked }))}
            />
          </div>
          <div>
            <Label className="text-wt-text-primary">Fallback Agent</Label>
            <Select 
              value={settings.fallback_user_id || 'none'} 
              onValueChange={(val) => setSettings(prev => ({ 
                ...prev, 
                fallback_user_id: val === 'none' ? null : val 
              }))}
            >
              <SelectTrigger className="mt-1.5 bg-wt-bg border-wt-border text-wt-text-primary">
                <SelectValue placeholder="Select fallback agent" />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border">
                <SelectItem value="none" className="text-wt-text-primary">No fallback</SelectItem>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.id} className="text-wt-text-primary">
                    {member.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-wt-text-muted mt-1">Receives conversations when no one else is available</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// ANALYTICS TAB
// ============================================================================
const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d'); // today, yesterday, 7d, 30d, 90d, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, customStartDate, customEndDate]);

  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dateRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = now;
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case '7d':
        startDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '30d':
        startDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '90d':
        startDate = new Date(now - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          startDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
          endDate = now;
        }
        break;
      default:
        startDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
    }
    
    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString()
    };
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = getDateParams();
      const response = await axios.get(`${API_BASE_URL}/inbox/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      case 'custom': return customStartDate && customEndDate 
        ? `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`
        : 'Custom Range';
      default: return 'Last 7 Days';
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-wt-text-muted">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p>Failed to load analytics data</p>
        <Button onClick={loadAnalytics} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const { overview, by_status, by_channel, by_direction, agents, daily_conversations, top_tags, response_time_stats, hourly_distribution } = analytics;
  
  // Calculate response rate
  const responseRate = overview?.total_conversations > 0 
    ? Math.round(((by_status?.resolved || 0) / overview.total_conversations) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with Date Filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Inbox Analytics</h3>
          <p className="text-sm text-wt-text-muted">Performance metrics for {getDateRangeLabel()}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px] bg-wt-surface border-wt-border text-wt-text-primary">
              <Calendar className="h-4 w-4 mr-2 text-wt-text-muted" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              <SelectItem value="today" className="text-wt-text-primary">Today</SelectItem>
              <SelectItem value="yesterday" className="text-wt-text-primary">Yesterday</SelectItem>
              <SelectItem value="7d" className="text-wt-text-primary">Last 7 Days</SelectItem>
              <SelectItem value="30d" className="text-wt-text-primary">Last 30 Days</SelectItem>
              <SelectItem value="90d" className="text-wt-text-primary">Last 90 Days</SelectItem>
              <SelectItem value="custom" className="text-wt-text-primary">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <DateInput
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-[140px] bg-wt-surface border-wt-border text-wt-text-primary"
              />
              <span className="text-wt-text-muted">to</span>
              <DateInput
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-[140px] bg-wt-surface border-wt-border text-wt-text-primary"
              />
            </div>
          )}
          
          <Button 
            onClick={loadAnalytics} 
            variant="outline" 
            className="border-wt-border text-wt-text-primary"
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{overview?.total_conversations || 0}</p>
                <p className="text-xs text-wt-text-muted">Total Conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20 text-green-400">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{overview?.total_messages || 0}</p>
                <p className="text-xs text-wt-text-muted">Total Messages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">{responseRate}%</p>
                <p className="text-xs text-wt-text-muted">Resolution Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                <Timer className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-wt-text-primary">
                  {response_time_stats?.avg_response_time || '-'}
                </p>
                <p className="text-xs text-wt-text-muted">Avg Response Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-wt-text-primary">Messages by Direction</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-wt-text-muted text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  Received (Inbound)
                </span>
                <span className="text-wt-text-primary font-semibold">{by_direction?.inbound || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-wt-text-muted text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  Sent (Outbound)
                </span>
                <span className="text-wt-text-primary font-semibold">{by_direction?.outbound || 0}</span>
              </div>
              {/* Visual bar */}
              <div className="h-4 bg-wt-paper rounded-full overflow-hidden flex mt-2">
                <div 
                  className="bg-emerald-500 transition-all"
                  style={{ 
                    width: `${(by_direction?.inbound || 0) / ((by_direction?.inbound || 0) + (by_direction?.outbound || 0) || 1) * 100}%` 
                  }}
                />
                <div 
                  className="bg-blue-500 transition-all"
                  style={{ 
                    width: `${(by_direction?.outbound || 0) / ((by_direction?.inbound || 0) + (by_direction?.outbound || 0) || 1) * 100}%` 
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-wt-text-primary">Conversations by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-wt-text-muted text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  Open
                </span>
                <span className="text-wt-text-primary font-semibold">{by_status?.open || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-wt-text-muted text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  Resolved
                </span>
                <span className="text-wt-text-primary font-semibold">{by_status?.resolved || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-wt-text-muted text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  Closed
                </span>
                <span className="text-wt-text-primary font-semibold">{by_status?.closed || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status & Channel Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-wt-text-primary">By Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${
                      status === 'open' ? 'bg-green-500' :
                      status === 'pending' ? 'bg-yellow-500' :
                      status === 'resolved' ? 'bg-blue-500' : 'bg-gray-500'
                    }`} />
                    <span className="text-sm text-wt-text-primary capitalize">{status}</span>
                  </div>
                  <span className="text-sm font-medium text-wt-text-primary">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-wt-text-primary">By Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(by_channel || {}).map(([channel, count]) => (
                <div key={channel} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${
                      channel === 'whatsapp' ? 'bg-green-500' :
                      channel === 'email' ? 'bg-blue-500' : 'bg-purple-500'
                    }`} />
                    <span className="text-sm text-wt-text-primary capitalize">{channel}</span>
                  </div>
                  <span className="text-sm font-medium text-wt-text-primary">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-wt-text-primary">
            <UserCheck className="h-4 w-4" />
            Agent Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agents && agents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-wt-border">
                    <th className="text-left py-2 text-sm font-medium text-wt-text-muted">Agent</th>
                    <th className="text-center py-2 text-sm font-medium text-wt-text-muted">Total</th>
                    <th className="text-center py-2 text-sm font-medium text-wt-text-muted">Open</th>
                    <th className="text-center py-2 text-sm font-medium text-wt-text-muted">Resolved</th>
                    <th className="text-center py-2 text-sm font-medium text-wt-text-muted">Messages Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, idx) => (
                    <tr key={idx} className="border-b border-wt-border last:border-0">
                      <td className="py-3 text-sm text-wt-text-primary">
                        {agent.user_name || 'Unknown'}
                      </td>
                      <td className="py-3 text-sm text-center text-wt-text-primary">
                        {agent.total_conversations}
                      </td>
                      <td className="py-3 text-sm text-center">
                        <Badge className="bg-green-500/20 text-green-400">{agent.open_conversations}</Badge>
                      </td>
                      <td className="py-3 text-sm text-center">
                        <Badge className="bg-blue-500/20 text-blue-400">{agent.resolved_conversations}</Badge>
                      </td>
                      <td className="py-3 text-sm text-center text-wt-text-primary">
                        {agent.messages_sent}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-wt-text-muted text-center py-4">No agent data available</p>
          )}
        </CardContent>
      </Card>

      {/* Daily Conversations Chart Placeholder */}
      {daily_conversations && daily_conversations.length > 0 && (
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-wt-text-primary">Conversations (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {daily_conversations.map((day, idx) => {
                const maxCount = Math.max(...daily_conversations.map(d => d.count), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-wt-gold rounded-t"
                      style={{ height: `${Math.max(height, 4)}%` }}
                    />
                    <span className="text-xs text-wt-text-muted">{day.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Tags */}
      {top_tags && top_tags.length > 0 && (
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-wt-text-primary">Top Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {top_tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="border-wt-border text-wt-text-primary">
                  {tag.tag} ({tag.count})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const InboxSettingsPage = () => {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-playfair text-wt-text-primary flex items-center gap-3">
          <Settings className="h-8 w-8 text-wt-gold" />
          Inbox Settings
        </h1>
        <p className="text-wt-text-muted mt-2">
          Configure canned responses, working hours, auto-assignment, and view analytics
        </p>
      </motion.div>

      {/* Tabs */}
      <Tabs defaultValue="canned" className="space-y-6">
        <TabsList className="bg-wt-surface border border-wt-border p-1 w-full sm:w-auto flex-wrap" data-testid="inbox-settings-tabs">
          <TabsTrigger 
            value="canned" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black flex items-center gap-2"
            data-testid="tab-canned-responses"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Canned Responses</span>
            <span className="sm:hidden">Responses</span>
          </TabsTrigger>
          <TabsTrigger 
            value="hours" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black flex items-center gap-2"
            data-testid="tab-working-hours"
          >
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Working Hours</span>
            <span className="sm:hidden">Hours</span>
          </TabsTrigger>
          <TabsTrigger 
            value="assign" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black flex items-center gap-2"
            data-testid="tab-auto-assign"
          >
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Auto-Assign</span>
            <span className="sm:hidden">Assign</span>
          </TabsTrigger>
          <TabsTrigger 
            value="analytics" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black flex items-center gap-2"
            data-testid="tab-analytics"
          >
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <TabsContent value="canned" className="mt-6">
            <CannedResponsesTab />
          </TabsContent>
          <TabsContent value="hours" className="mt-6">
            <WorkingHoursTab />
          </TabsContent>
          <TabsContent value="assign" className="mt-6">
            <AutoAssignTab />
          </TabsContent>
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsTab />
          </TabsContent>
        </motion.div>
      </Tabs>
    </div>
  );
};

export default InboxSettingsPage;
